import React from 'react';
import { cn } from '@utils/cn';
import { Loading } from '@components/atoms/Loading';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary';
  onlyIcon?: boolean;
  size?: 'md' | 'lg';
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  isSelected?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      leftIcon,
      rightIcon,
      disabled,
      loading = false,
      fullWidth = false,
      className,
      isSelected = false,
      size = 'md',
      variant = 'primary',
      onlyIcon = false,
      type: buttonType = 'button',
      ...props
    },
    ref,
  ) => {
    const baseStyles = [
      'flex items-center justify-center font-medium rounded-md transition-all duration-200',
      'focus:outline-none gap-2',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800',
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
      'active:scale-95',
    ];

    const sizeStyles = {
      md: 'px-4 py-2 text-xs min-h-8 max-h-8',
      lg: 'px-6 py-2.5 text-sm min-h-10 max-h-10',
    };

    const onlyIconSizeStyles = {
      md: 'w-8 h-8 min-w-8 min-h-8 text-xs',
      lg: 'w-10 h-10 min-w-10 min-h-10 text-sm',
    };

    const variantStyles = {
      primary: [
        'bg-indigo-900 dark:bg-indigo-700 text-white',
        'hover:bg-indigo-700 dark:hover:bg-indigo-600',
        'active:bg-indigo-800 dark:active:bg-indigo-500',
        isSelected &&
          'bg-indigo-800 text-white dark:bg-indigo-600 dark:text-white',
      ],
      secondary: [
        'bg-gray-100 dark:bg-gray-500 text-gray-900 dark:text-white',
        'hover:bg-gray-200 dark:hover:bg-gray-600',
        'active:bg-gray-300 dark:active:bg-gray-500',
        isSelected &&
          'bg-gray-300 text-gray-900 dark:bg-gray-600 dark:text-white',
      ],
      tertiary: [
        'bg-white dark:bg-transparent text-gray-900 dark:text-white',
        'border border-gray-200 dark:border-gray-600',
        'hover:bg-indigo-500 hover:text-white dark:hover:bg-gray-700',
        'active:bg-indigo-300 active:text-neutral-800 dark:active:bg-gray-600',
        isSelected &&
          'bg-indigo-700 text-indigo-600 border-indigo-600 shadow-sm dark:bg-indigo-600 dark:text-white hover:bg-indigo-700 dark:hover:bg-indigo-500',
      ],
    };

    const finalSizeStyles = onlyIcon
      ? onlyIconSizeStyles[size]
      : sizeStyles[size];

    const widthStyles = fullWidth && !onlyIcon ? 'w-full' : '';

    const iconStyles = 'flex-shrink-0 text-xs';
    // The icon margins exist to separate the icon from the label. With `onlyIcon` there is no
    // label, so keeping them pushes the icon off-centre inside the fixed square — visibly so at
    // the `md` size, where `mr-2` is a quarter of the 32px button.
    const leftIconStyles = leftIcon && !onlyIcon ? 'mr-2' : '';
    const rightIconStyles = rightIcon && !onlyIcon ? 'ml-2' : '';

    // Twelve call sites pass the glyph as `children` with `onlyIcon` set and no icon prop
    // — ProjectCard, UserCard, ActionButtons, CrewHeader and friends. For those, `children`
    // IS the icon, so hiding it hides the icon. Only suppress the label when an explicit
    // icon prop supplied the glyph instead.
    const hidesLabel = onlyIcon && Boolean(leftIcon || rightIcon);

    if (
      process.env.NODE_ENV === 'development' &&
      onlyIcon &&
      !leftIcon &&
      !rightIcon
    ) {
      console.warn(
        '[Button] `onlyIcon` is true but no icon (leftIcon or rightIcon) was provided. ' +
          'The children are rendered as the icon; prefer passing it via `leftIcon`.',
      );
    }

    return (
      <button
        ref={ref}
        // `type` defaults to 'button' above; a caller's explicit 'submit'/'reset' wins. The rule
        // cannot see through the variable, and every raw <button> elsewhere is checked statically.
        // eslint-disable-next-line react/button-has-type
        type={buttonType}
        className={cn(
          className,
          baseStyles,
          finalSizeStyles,
          variantStyles[variant],
          widthStyles,
        )}
        disabled={disabled || loading}
        {...props}>
        {loading && <Loading size="md" />}
        <div className="flex items-center justify-center w-full">
          {leftIcon && (
            <span className={cn(iconStyles, leftIconStyles)}>{leftIcon}</span>
          )}
          {!hidesLabel && <span className="text-center">{children}</span>}
          {rightIcon && (
            <span className={cn(iconStyles, rightIconStyles)}>{rightIcon}</span>
          )}
        </div>
      </button>
    );
  },
);
